import { useQuery } from '@tanstack/react-query';
import {
  ISchedulePreviewContext,
  ISchedulePreviewWithChangeSetContext,
} from 'src/interfaces/SchedulePreview';
import { queryKeys, requestCCApi } from 'src/utils';

export const useGetSchedulePreviewContext = (
  locationGroupId: string,
  locationCode?: string,
  changeSetIds?: string | string[],
  includeBefore?: boolean, // Defaults to `true`; OO sets this to `false` because it only needs the "after" JSON.
) => {
  const changeSetIdsValue = Array.isArray(changeSetIds)
    ? changeSetIds.join('|')
    : changeSetIds;

  return useQuery({
    queryKey: queryKeys.schedulePreview.details([
      locationGroupId,
      locationCode,
      changeSetIdsValue,
      includeBefore,
    ]),
    queryFn: () =>
      fetchSchedulePreviewData(
        locationGroupId,
        locationCode,
        changeSetIds,
        includeBefore,
      ),
    enabled: Boolean(locationGroupId),
    keepPreviousData: true,
  });
};

export const fetchSchedulePreviewData = async (
  locationGroupId: string,
  locationCode?: string,
  changeSetIds?: string | string[],
  includeBefore?: boolean,
): Promise<ISchedulePreviewContext | ISchedulePreviewWithChangeSetContext> => {
  const hasMultipleChangeSetIds =
    Array.isArray(changeSetIds) && changeSetIds.length > 1;
  const changeSetIdsValue = Array.isArray(changeSetIds)
    ? changeSetIds.join('|')
    : changeSetIds;
  const includeBeforeParam =
    changeSetIdsValue && includeBefore !== undefined
      ? String(includeBefore)
      : undefined;

  const params = new URLSearchParams(
    Object.fromEntries(
      Object.entries({
        location_group_id: locationGroupId,
        location_code: locationCode,
        change_set_id: !hasMultipleChangeSetIds ? changeSetIdsValue : undefined,
        'change_set_id.list': hasMultipleChangeSetIds
          ? changeSetIdsValue
          : undefined,
        include_before: includeBeforeParam,
      }).filter(([_, value]) => value !== undefined) as Array<string[]>,
    ),
  );

  const response = await requestCCApi({ url: `/preview?${params}` });

  return response;
};
